import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, deleteField, doc, getDoc, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  ArrowLeft,
  Trash2,
  Building2,
  FileText,
  CreditCard,
  Briefcase,
  StickyNote,
  Plus,
  Folder,
  FolderPlus,
  FolderInput,
  Pencil,
  Search,
  Tag,
  ChevronDown,
} from 'lucide-react';
import { auth, db, storage } from '../../firebase';
import Modal from '../../components/Modal';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import BBALoader from '../../components/BBALoader';
import { getTemplateViewUrl } from '../../utils/hrTemplates';
import {
  normalizePartnerDoc,
  updatePartner,
  enrichMembers,
  buildMemberWrite,
  timestampToDateInput,
} from '../../services/partnersService';
// Document/folder shapes are shared with the partner member's My Documents page.
import {
  newId,
  DOC_UPLOAD_ACCEPT,
  DOC_TAGS,
  serializeDocumentsForFirestore,
  normalizeDocFromPartner,
  serializeFoldersForFirestore,
} from '../../utils/partnerDocuments';
import {
  getPartnerExpenses,
  savePartnerExpense,
  deletePartnerExpense,
  parseExpenseDate,
  sumExpenses,
  sumExpensesForMonth,
} from '../../services/accountExpensesService';
import PortalBackground from '../../components/PortalBackground';

const LOGO_UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.svg,.webp';

function serializeProjectsForFirestore(projects) {
  return (projects || [])
    .filter((p) => (p.name || '').trim() || (p.notes || '').trim())
    .map((p) => ({
      id: String(p.id || newId()),
      name: String(p.name || '').trim(),
      status: String(p.status || 'Active'),
      value: String(p.value || '').trim(),
      notes: String(p.notes || '').trim(),
    }));
}

function mergeLegacyDocuments(raw, normalized) {
  if (raw.documents !== undefined) return null;
  const merged = [];
  if (normalized.w9FileUrl && String(normalized.w9FileUrl).trim()) {
    merged.push({
      id: newId(),
      name: 'W-9',
      fileUrl: normalized.w9FileUrl.trim(),
      uploadedAt: new Date(),
      uploadedByUid: '',
      uploaderName: '',
    });
  }
  if (Array.isArray(normalized.contracts)) {
    normalized.contracts.forEach((c) => {
      if (c.url && String(c.url).trim()) {
        merged.push({
          id: String(c.id || newId()),
          name: String(c.title || 'Contract').trim() || 'Contract',
          fileUrl: String(c.url).trim(),
          uploadedAt: new Date(),
          uploadedByUid: '',
          uploaderName: '',
        });
      }
    });
  }
  return merged.length ? merged : null;
}

const TEAMS =['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
const PROJECT_STATUSES = ['Active', 'On Hold', 'Completed', 'Cancelled'];

/** Sentinel for the expense modal's client picker — stored as accountId ''. */
const INTERNAL_EXPENSE = '__internal__';
const EMPTY_EXPENSE_FORM = {
  amount: '',
  date: '',
  status: 'paid',
  description: '',
  accountChoice: '',
};
const EXPENSE_STATUS_STYLES = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  processing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
const formatMoney = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const TABS = [
  { id: 'summary', name: 'Summary', icon: Building2 },
  { id: 'documents', name: 'Documents', icon: FileText },
  { id: 'billing', name: 'Billing', icon: CreditCard },
  { id: 'projects', name: 'Projects', icon: Briefcase },
  { id: 'notes', name: 'Notes', icon: StickyNote },
];

const inputClass =
  'w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const sectionClass =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm';
const outlineBtnSm =
  'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
const rowIconBtn =
  'p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700';
const rowIconBtnDanger =
  'p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950';
const modalCancelBtn =
  'px-4 py-2 rounded-md text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
const modalPrimaryBtn =
  'px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50';

const PartnerOverview = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [isEditing, setIsEditing] = useState(false);
  const [enrichedMembers, setEnrichedMembers] = useState([]);

  // Summary
  const [companyName, setCompanyName] = useState('');
  const [team, setTeam] = useState('General');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');

  // Team
  const [memberRows, setMemberRows] = useState([{ userId: '', dateJoined: '' }]);
  const [internalUsersList, setInternalUsersList] = useState([]);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [docSearch, setDocSearch] = useState('');
  const [tagDocId, setTagDocId] = useState(null); // doc whose name/tags are being edited
  const [docNameDraft, setDocNameDraft] = useState(''); // rename field in the doc editor modal
  const [deleteDocId, setDeleteDocId] = useState(null); // doc pending delete confirmation
  const [moveDocId, setMoveDocId] = useState(null); // doc being moved to a folder
  const [folderModal, setFolderModal] = useState(null); // null | { id: string|null } (null id = new)
  const [folderName, setFolderName] = useState('');
  const [dragDocId, setDragDocId] = useState(null); // doc row being dragged
  const [dropFolderId, setDropFolderId] = useState(null); // folder row hovered during a drag
  const [viewerDoc, setViewerDoc] = useState(null); // { url, title } for the fullscreen viewer

  // Billing — expenses for this partner across client accounts + internal
  const [expenses, setExpenses] = useState([]);
  const [expensesLoaded, setExpensesLoaded] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [expenseModal, setExpenseModal] = useState(null); // null | { original: expense|null }
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE_FORM);
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [expenseError, setExpenseError] = useState(null);

  // Projects
  const [projects, setProjects] = useState([]);

  // Notes
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const snap = await getDoc(doc(db, 'partners', partnerId));
      if (!snap.exists()) {
        setNotFound(true);
        setError('Partner not found.');
        return;
      }
      const raw = snap.data();
      const p = normalizePartnerDoc(snap.id, raw);

      const usersSnap = await getDocs(collection(db, 'internal_users'));
      setCompanyName(p.companyName || '');
      setTeam(p.team || 'General');
      setLogoUrl(raw.logoUrl || '');
      setWebsite(raw.website || '');
      setPhone(raw.phone || '');
      setNotes(raw.notes || '');
      setProjects(
        Array.isArray(raw.projects)
          ? raw.projects.map((pr) => ({
              id: pr.id || newId(),
              name: pr.name || '',
              status: pr.status || 'Active',
              value: pr.value || '',
              notes: pr.notes || '',
            }))
          : []
      );

      const list = [];
      usersSnap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      list.sort((a, b) =>
        `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
          `${b.firstName || ''} ${b.lastName || ''}`,
          undefined,
          { sensitivity: 'base' }
        )
      );
      setInternalUsersList(list);

      const rows =
        p.members && p.members.length > 0
          ? p.members.map((m) => {
              const dj = m.dateJoined;
              let dateJoined = '';
              if (dj instanceof Date) {
                dateJoined = timestampToDateInput({ toDate: () => dj });
              } else if (dj && typeof dj.toDate === 'function') {
                dateJoined = timestampToDateInput(dj);
              }
              return {
                userId: m.userId || '',
                dateJoined,
              };
            })
          : [{ userId: '', dateJoined: '' }];
      setMemberRows(rows);

      if (raw.documents === undefined) {
        const merged = mergeLegacyDocuments(raw, p);
        setDocuments(merged || []);
      } else {
        setDocuments(Array.isArray(raw.documents) ? raw.documents.map((d) => normalizeDocFromPartner(d)) : []);
      }
      setFolders(
        Array.isArray(raw.documentFolders)
          ? raw.documentFolders.map((f) => ({
              id: String(f.id || newId()),
              name: String(f.name || 'Folder'),
              createdByName: String(f.createdByName || ''),
              createdAt:
                f.createdAt && typeof f.createdAt.toDate === 'function' ? f.createdAt.toDate() : null,
            }))
          : []
      );
      const enriched = await enrichMembers(p.members);
      setEnrichedMembers(enriched);
    } catch (e) {
      console.error(e);
      setError('Failed to load partner.');
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Billing (expenses) ----
  // Clients (picker + whose expenses to read), then this partner's expenses across them
  const loadExpenses = useCallback(async () => {
    if (!partnerId) return;
    setExpensesLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
      const clients = snap.docs
        .map((d) => ({ id: d.id, company: d.data().company || d.data().contact?.name || d.id }))
        .sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }));
      setClientOptions(clients);
      setExpenses(await getPartnerExpenses(partnerId, companyName, clients.map((c) => c.id)));
      setExpenseError(null);
    } catch (e) {
      console.error('Error loading partner expenses:', e);
      setExpenseError('Failed to load expenses.');
    } finally {
      setExpensesLoading(false);
      setExpensesLoaded(true);
    }
  }, [partnerId, companyName]);

  // Loads the first time the Billing tab opens
  useEffect(() => {
    if (activeTab !== 'billing' || expensesLoaded || loading) return;
    loadExpenses();
  }, [activeTab, expensesLoaded, loading, loadExpenses]);

  const clientNameById = useMemo(
    () => new Map(clientOptions.map((c) => [c.id, c.company])),
    [clientOptions]
  );

  const openExpenseModal = (expense = null) => {
    setExpenseError(null);
    setExpenseForm(
      expense
        ? {
            amount: expense.amount ?? '',
            date: expense.date || '',
            status: expense.status || 'paid',
            description: expense.description || '',
            accountChoice: expense.accountId || INTERNAL_EXPENSE,
          }
        : EMPTY_EXPENSE_FORM
    );
    setExpenseModal({ original: expense });
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.amount || !expenseForm.accountChoice) return;
    setExpenseSaving(true);
    setExpenseError(null);
    try {
      await savePartnerExpense(
        {
          amount: expenseForm.amount,
          date: expenseForm.date,
          status: expenseForm.status,
          description: expenseForm.description.trim(),
          subcontractorId: partnerId,
          subcontractorName: companyName,
        },
        expenseForm.accountChoice === INTERNAL_EXPENSE ? '' : expenseForm.accountChoice,
        expenseModal?.original || null
      );
      setExpenseModal(null);
      await loadExpenses();
    } catch (e) {
      console.error('Error saving expense:', e);
      setExpenseError(e.message || 'Failed to save expense.');
    } finally {
      setExpenseSaving(false);
    }
  };

  const handleDeleteExpense = async () => {
    const original = expenseModal?.original;
    if (!original) return;
    const note = original.accountId ? ' This also removes it from the client’s Billing tab.' : '';
    if (!window.confirm(`Delete this expense?${note}`)) return;
    setExpenseSaving(true);
    try {
      await deletePartnerExpense(original);
      setExpenseModal(null);
      await loadExpenses();
    } catch (e) {
      console.error('Error deleting expense:', e);
      setExpenseError(e.message || 'Failed to delete expense.');
    } finally {
      setExpenseSaving(false);
    }
  };

  const uploadToStorage = async (storagePath, file) => {
    if (!auth.currentUser) throw new Error('You must be signed in to upload files.');
    const safeName = (file.name || 'file').replace(/[^\w.\-]+/g, '_');
    const fullPath = `${storagePath}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, fullPath);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // ---- Logo ----
  const handleLogoUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = LOGO_UPLOAD_ACCEPT;
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file || !partnerId) return;
      setLogoUploading(true);
      setError(null);
      try {
        const url = await uploadToStorage(`partners/${partnerId}/logo`, file);
        setLogoUrl(url);
        await updatePartner(partnerId, { logoUrl: url });
      } catch (err) {
        console.error(err);
        setError(err.message || 'Logo upload failed');
      } finally {
        setLogoUploading(false);
      }
    };
    input.click();
  };

  const handleRemoveLogo = async () => {
    if (!partnerId) return;
    setLogoUrl('');
    setError(null);
    try {
      await updatePartner(partnerId, { logoUrl: '' });
    } catch (err) {
      console.error(err);
      setError('Failed to remove logo');
      await load();
    }
  };

  // ---- Documents ----
  const persistDocuments = async (nextDocs) => {
    if (!partnerId) return;
    await updatePartner(partnerId, {
      documents: serializeDocumentsForFirestore(nextDocs),
      contracts: [],
      w9FileUrl: '',
      w9Collected: false,
      w9Notes: '',
      w9CollectedAt: deleteField(),
    });
  };

  const handleAddDocumentClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = DOC_UPLOAD_ACCEPT;
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file || !partnerId) return;
      setDocUploading(true);
      setError(null);
      try {
        const docId = newId();
        const url = await uploadToStorage(`partners/${partnerId}/documents/${docId}`, file);
        const u = auth.currentUser;
        const entry = {
          id: docId,
          name: file.name || 'Document',
          fileName: file.name || '',
          fileUrl: url,
          folderId: currentFolderId || '',
          tagIds: [],
          uploadedAt: new Date(),
          uploadedByUid: u?.uid || '',
          uploaderName: u?.displayName || u?.email || 'Unknown',
        };
        const next = [...documents, entry];
        setDocuments(next);
        await persistDocuments(next);
        // Let the user rename and tag the file right after it lands.
        openDocEditor(entry);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Upload failed');
      } finally {
        setDocUploading(false);
      }
    };
    input.click();
  };

  const handleRemoveDocument = async (docId) => {
    if (!partnerId) return;
    const next = documents.filter((d) => d.id !== docId);
    setDocuments(next);
    setDeleteDocId(null);
    setError(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to remove document');
      await load();
    }
  };

  // Open the rename + tag editor for a document (used post-upload and from the row).
  const openDocEditor = (docEntry) => {
    setTagDocId(docEntry.id);
    setDocNameDraft(docEntry.name || '');
  };

  const commitDocRename = async () => {
    const name = docNameDraft.trim();
    const target = documents.find((d) => d.id === tagDocId);
    if (!target || !name || name === target.name) return;
    const next = documents.map((d) => (d.id === tagDocId ? { ...d, name } : d));
    setDocuments(next);
    setError(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to rename document');
      await load();
    }
  };

  const closeDocEditor = async () => {
    await commitDocRename();
    setTagDocId(null);
  };

  // Open the file in a fullscreen in-page viewer (same as portal sales resources):
  // PDFs/images render natively, Office files via the Office web viewer.
  const openDocument = (documentEntry) => {
    const url = getTemplateViewUrl({
      fileUrl: documentEntry.fileUrl,
      fileName: documentEntry.fileName || documentEntry.name,
    });
    if (url) setViewerDoc({ url, title: documentEntry.name });
  };

  const formatUploaded = (d) => {
    if (!d) return 'Not set';
    if (d instanceof Date) return d.toLocaleDateString();
    if (d.toDate) return d.toDate().toLocaleDateString();
    return 'Not set';
  };

  // ---- Folders ----
  const saveFolder = async () => {
    const name = folderName.trim();
    if (!name || !partnerId) return;
    const u = auth.currentUser;
    const next = folderModal?.id
      ? folders.map((f) => (f.id === folderModal.id ? { ...f, name } : f))
      : [
          ...folders,
          {
            id: newId(),
            name,
            createdByName: u?.displayName || u?.email || 'Unknown',
            createdAt: new Date(),
          },
        ];
    setFolders(next);
    setFolderModal(null);
    setError(null);
    try {
      await updatePartner(partnerId, { documentFolders: serializeFoldersForFirestore(next) });
    } catch (err) {
      console.error(err);
      setError('Failed to save folder');
      await load();
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Its documents move back to All files.')) return;
    const nextFolders = folders.filter((f) => f.id !== folderId);
    const nextDocs = documents.map((d) => (d.folderId === folderId ? { ...d, folderId: '' } : d));
    setFolders(nextFolders);
    setDocuments(nextDocs);
    if (currentFolderId === folderId) setCurrentFolderId(null);
    setError(null);
    try {
      await updatePartner(partnerId, {
        documentFolders: serializeFoldersForFirestore(nextFolders),
        documents: serializeDocumentsForFirestore(nextDocs),
      });
    } catch (err) {
      console.error(err);
      setError('Failed to delete folder');
      await load();
    }
  };

  const moveDocumentToFolder = async (docId, folderId) => {
    const next = documents.map((d) => (d.id === docId ? { ...d, folderId } : d));
    setDocuments(next);
    setMoveDocId(null);
    setError(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to move document');
      await load();
    }
  };

  // ---- Tags (fixed set from DOC_TAGS) ----
  const toggleDocTag = async (docId, tagId) => {
    const next = documents.map((d) => {
      if (d.id !== docId) return d;
      const has = (d.tagIds || []).includes(tagId);
      return {
        ...d,
        tagIds: has ? d.tagIds.filter((x) => x !== tagId) : [...(d.tagIds || []), tagId],
      };
    });
    setDocuments(next);
    setError(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to update tags');
      await load();
    }
  };

  // ---- Team ----
  const setMemberRow = (index, field, value) => {
    setMemberRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addMemberRow = () => {
    setMemberRows((prev) => [...prev, { userId: '', dateJoined: '' }]);
  };

  const removeMemberRow = (index) => {
    setMemberRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  // ---- Projects ----
  const setProjectField = (index, field, value) => {
    setProjects((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addProject = () => {
    setProjects((prev) => [...prev, { id: newId(), name: '', status: 'Active', value: '', notes: '' }]);
  };

  const removeProject = (index) => {
    setProjects((prev) => prev.filter((_, i) => i !== index));
  };

  // ---- Save (all editable fields) ----
  const handleSaveOverview = async (e) => {
    e.preventDefault();
    if (!partnerId) return;
    setSaving(true);
    setError(null);
    try {
      const members = memberRows
        .map((row) =>
          buildMemberWrite({
            userId: row.userId,
            dateJoinedInput: row.dateJoined,
          })
        )
        .filter(Boolean);

      await updatePartner(partnerId, {
        companyName: companyName.trim(),
        team,
        logoUrl,
        website: website.trim(),
        phone: phone.trim(),
        members,
        documents: serializeDocumentsForFirestore(documents),
        documentFolders: serializeFoldersForFirestore(folders),
        projects: serializeProjectsForFirestore(projects),
        notes: notes.trim(),
        contracts: [],
        w9FileUrl: '',
        w9Collected: false,
        w9Notes: '',
        w9CollectedAt: deleteField(),
      });
      await load();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6">
        <button
          type="button"
          onClick={() => navigate('/crm/partners')}
          className="text-red-600 dark:text-red-400 flex items-center gap-2 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to partners
        </button>
        <p className="text-gray-800 dark:text-gray-200">{error || 'Partner not found.'}</p>
      </div>
    );
  }

  // ---------- Tab content ----------
  const renderSummaryTab = () => (
    <div className="space-y-8">
    <section className={sectionClass}>
      <div className="flex flex-col sm:flex-row items-center gap-5 mb-6">
        <div className="w-28 h-28 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
          ) : (
            <Building2 className="w-10 h-10 text-gray-400" />
          )}
        </div>
        {isEditing && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleLogoUploadClick}
              disabled={logoUploading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              {logoUploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              >
                Remove logo
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Company name</label>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={!isEditing}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Department</label>
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            disabled={!isEditing}
            className={inputClass}
          >
            {TEAMS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Website</label>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://…"
            disabled={!isEditing}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={!isEditing}
            className={inputClass}
          />
        </div>
      </div>
    </section>

    <section className={sectionClass}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team members</h2>
        {isEditing && (
          <button
            type="button"
            onClick={addMemberRow}
            className="text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            + Add member
          </button>
        )}
      </div>
      {isEditing && (
      <div className="space-y-3 mb-6">
        {memberRows.map((row, i) => (
          <div
            key={i}
            className="flex flex-col gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-gray-500 dark:text-gray-400">Internal user</label>
                <select
                  value={row.userId}
                  onChange={(e) => setMemberRow(i, 'userId', e.target.value)}
                  className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Select…</option>
                  {internalUsersList.map((u) => (
                    <option key={u.id} value={u.id}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} — {u.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-40">
                <label className="text-xs text-gray-500 dark:text-gray-400">Date joined</label>
                <input
                  type="date"
                  value={row.dateJoined}
                  onChange={(e) => setMemberRow(i, 'dateJoined', e.target.value)}
                  className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
              {memberRows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeMemberRow(i)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {enrichedMembers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700 rounded">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="text-left p-2 text-gray-700 dark:text-gray-200">Name</th>
                <th className="text-left p-2 text-gray-700 dark:text-gray-200">Email</th>
                <th className="text-left p-2 text-gray-700 dark:text-gray-200">Role</th>
                <th className="text-left p-2 text-gray-700 dark:text-gray-200">Joined</th>
              </tr>
            </thead>
            <tbody>
              {enrichedMembers.map((m) => (
                <tr key={m.userId} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="p-2 text-gray-900 dark:text-white">
                    {m.missing ? '—' : `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—'}
                  </td>
                  <td className="p-2 text-gray-700 dark:text-gray-300">{m.missing ? '—' : m.email}</td>
                  <td className="p-2 text-gray-700 dark:text-gray-300">{m.missing ? '—' : m.role}</td>
                  <td className="p-2 text-gray-700 dark:text-gray-300">
                    {m.dateJoined instanceof Date
                      ? m.dateJoined.toLocaleDateString()
                      : m.dateJoined?.toDate
                        ? m.dateJoined.toDate().toLocaleDateString()
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No team members yet.</p>
      )}
    </section>
    </div>
  );

  const renderDocumentsTab = () => {
    const inFolder = folders.find((f) => f.id === currentFolderId) || null;
    const activeTag = DOC_TAGS.find((t) => t.id === tagFilter) || null;
    const q = docSearch.trim().toLowerCase();
    const searching = q.length > 0;
    // A search or tag filter spans all folders; otherwise show the current folder's contents.
    const visibleFolders = tagFilter || currentFolderId || searching ? [] : folders;
    const visibleDocs = documents.filter((d) => {
      if (q && !(d.name || '').toLowerCase().includes(q)) return false;
      if (tagFilter) return (d.tagIds || []).includes(tagFilter);
      if (searching) return true; // search spans all folders
      return (d.folderId || '') === (currentFolderId || '');
    });
    const docCountIn = (folderId) => documents.filter((d) => (d.folderId || '') === folderId).length;
    const cellSubtle = 'p-2.5 text-gray-600 dark:text-gray-400';

    return (
      <section className={sectionClass}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              placeholder="Search documents…"
              className="w-full pl-10 pr-3 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={tagFilter || 'all'}
                onChange={(e) => setTagFilter(e.target.value === 'all' ? null : e.target.value)}
                className="pl-7 pr-8 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500"
                title="Filter by tag"
              >
                <option value="all">All tags</option>
                {DOC_TAGS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button
              type="button"
              onClick={() => {
                setFolderModal({ id: null });
                setFolderName('');
              }}
              className={outlineBtnSm}
            >
              <FolderPlus className="w-4 h-4" /> New folder
            </button>
            <button
              type="button"
              disabled={docUploading}
              onClick={handleAddDocumentClick}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white"
            >
              <Plus className="w-4 h-4" /> {docUploading ? 'Uploading…' : 'Add document'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-sm mb-3">
          <button
            type="button"
            onClick={() => setCurrentFolderId(null)}
            onDragOver={(e) => {
              if (dragDocId && currentFolderId) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragDocId && currentFolderId) moveDocumentToFolder(dragDocId, '');
              setDragDocId(null);
              setDropFolderId(null);
            }}
            className={`${
              inFolder
                ? 'text-red-600 dark:text-red-400 hover:underline font-medium'
                : 'text-gray-900 dark:text-white font-medium cursor-default'
            } ${dragDocId && inFolder ? 'ring-1 ring-red-400 rounded px-1' : ''}`}
          >
            All files
          </button>
          {inFolder && (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-900 dark:text-white font-medium">{inFolder.name}</span>
            </>
          )}
          {activeTag && (
            <span className="text-gray-500 dark:text-gray-400">
              · tagged “{activeTag.name}” (all folders)
            </span>
          )}
        </div>

        {visibleFolders.length === 0 && visibleDocs.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-white/70 dark:bg-gray-900/50">
            <FileText className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 dark:text-gray-300">
              {searching
                ? 'No documents match your search.'
                : tagFilter
                  ? 'No documents with this tag.'
                  : inFolder
                    ? 'This folder is empty.'
                    : 'No documents yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                  <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200">Name</th>
                  <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200">Tags</th>
                  <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Uploaded by</th>
                  <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Uploaded</th>
                  <th className="p-2.5 w-28" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {visibleFolders.map((folder) => (
                  <tr
                    key={folder.id}
                    onClick={() => setCurrentFolderId(folder.id)}
                    onDragOver={(e) => {
                      if (dragDocId) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDragEnter={() => dragDocId && setDropFolderId(folder.id)}
                    onDragLeave={() => setDropFolderId((prev) => (prev === folder.id ? null : prev))}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragDocId) moveDocumentToFolder(dragDocId, folder.id);
                      setDragDocId(null);
                      setDropFolderId(null);
                    }}
                    className={`border-t border-gray-100 dark:border-gray-800 cursor-pointer ${
                      dropFolderId === folder.id
                        ? 'bg-red-50 dark:bg-red-950/40 ring-2 ring-inset ring-red-400'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                    }`}
                  >
                    <td className="p-2.5">
                      <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white pointer-events-none">
                        <Folder className="w-4 h-4 text-amber-500 shrink-0" />
                        {folder.name}
                        <span className="text-xs font-normal text-gray-400">({docCountIn(folder.id)})</span>
                      </span>
                    </td>
                    <td className={cellSubtle}>—</td>
                    <td className={`${cellSubtle} whitespace-nowrap`}>{folder.createdByName || '—'}</td>
                    <td className={`${cellSubtle} whitespace-nowrap`}>
                      {folder.createdAt ? formatUploaded(folder.createdAt) : '—'}
                    </td>
                    <td className="p-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFolderModal({ id: folder.id });
                            setFolderName(folder.name);
                          }}
                          className={rowIconBtn}
                          title="Rename folder"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id);
                          }}
                          className={rowIconBtnDanger}
                          title="Delete folder"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleDocs.map((documentEntry) => (
                  <tr
                    key={documentEntry.id}
                    draggable
                    onDragStart={(e) => {
                      setDragDocId(documentEntry.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', documentEntry.id);
                    }}
                    onDragEnd={() => {
                      setDragDocId(null);
                      setDropFolderId(null);
                    }}
                    onClick={() => openDocument(documentEntry)}
                    className={`border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer ${
                      dragDocId === documentEntry.id ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="p-2.5">
                      <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="truncate max-w-[260px]">{documentEntry.name}</span>
                      </span>
                    </td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(documentEntry.tagIds || []).map((tid) => {
                          const t = DOC_TAGS.find((x) => x.id === tid);
                          return t ? (
                            <span
                              key={tid}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
                              style={{ backgroundColor: t.color }}
                            >
                              {t.name}
                            </span>
                          ) : null;
                        })}
                        {(documentEntry.tagIds || []).length === 0 && (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className={`${cellSubtle} whitespace-nowrap`}>{documentEntry.uploaderName || '—'}</td>
                    <td className={`${cellSubtle} whitespace-nowrap`}>{formatUploaded(documentEntry.uploadedAt)}</td>
                    <td className="p-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocEditor(documentEntry);
                          }}
                          className={rowIconBtn}
                          title="Rename & tags"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {folders.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveDocId(documentEntry.id);
                            }}
                            className={rowIconBtn}
                            title="Move to folder"
                          >
                            <FolderInput className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDocId(documentEntry.id);
                          }}
                          className={rowIconBtnDanger}
                          title="Remove document"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderBillingTab = () => {
    const pendingTotal = expenses
      .filter((e) => e.status === 'processing')
      .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
    const stats = [
      { label: 'Paid (lifetime)', value: formatMoney(sumExpenses(expenses)) },
      { label: 'Paid this month', value: formatMoney(sumExpensesForMonth(expenses)) },
      { label: 'Processing', value: formatMoney(pendingTotal) },
    ];
    const thClass =
      'px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';

    return (
      <section className={sectionClass}>
        <div className="relative flex items-center justify-center mb-4 min-h-[36px]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center">Expenses</h2>
          <button
            type="button"
            onClick={() => openExpenseModal()}
            className="absolute right-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Expense</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 text-center"
            >
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{s.label}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">{s.value}</div>
            </div>
          ))}
        </div>

        {expenseError && !expenseModal && (
          <p className="mb-3 text-sm text-center text-red-600 dark:text-red-400">{expenseError}</p>
        )}

        {expensesLoading && !expensesLoaded ? (
          <div className="flex justify-center py-10">
            <BBALoader size={36} />
          </div>
        ) : expenses.length === 0 ? (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
            No expenses yet. Expenses marked for this partner on a client’s Billing tab show up here, or add one
            with <span className="font-medium">+ Expense</span>.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={`${thClass} text-left`}>Date</th>
                  <th className={`${thClass} text-left`}>Client</th>
                  <th className={`${thClass} text-left`}>Description</th>
                  <th className={`${thClass} text-right`}>Amount</th>
                  <th className={`${thClass} text-center`}>Status</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {expenses.map((expense) => {
                  const date = parseExpenseDate(expense);
                  return (
                    <tr key={`${expense.accountId || 'internal'}-${expense.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {date ? date.toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {expense.accountId ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/crm/client/${expense.accountId}`)}
                            className="text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400 hover:underline"
                          >
                            {clientNameById.get(expense.accountId) || 'Client'}
                          </button>
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            Internal
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate">
                        {expense.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-red-600 dark:text-red-400 whitespace-nowrap">
                        -{formatMoney(Math.abs(expense.amount || 0))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full capitalize ${EXPENSE_STATUS_STYLES[expense.status] || EXPENSE_STATUS_STYLES.processing}`}
                        >
                          {expense.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openExpenseModal(expense)}
                          className={rowIconBtn}
                          title="Edit expense"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderProjectsTab = () => (
    <section className={sectionClass}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Projects</h2>
        {isEditing && (
          <button
            type="button"
            onClick={addProject}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            <Plus className="w-4 h-4" />
            Add project
          </button>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-white/70 dark:bg-gray-900/50">
          <Briefcase className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-300">No projects yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((pr, i) => (
            <div
              key={pr.id}
              className="p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[180px]">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Project name</label>
                  <input
                    value={pr.name}
                    onChange={(e) => setProjectField(i, 'name', e.target.value)}
                    disabled={!isEditing}
                    className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
                  />
                </div>
                <div className="w-40">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                  <select
                    value={pr.status}
                    onChange={(e) => setProjectField(i, 'status', e.target.value)}
                    disabled={!isEditing}
                    className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-32">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Value</label>
                  <input
                    value={pr.value}
                    onChange={(e) => setProjectField(i, 'value', e.target.value)}
                    placeholder="$"
                    disabled={!isEditing}
                    className="w-full mt-0.5 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
                  />
                </div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => removeProject(i)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded"
                    title="Remove project"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Notes (optional)"
                value={pr.notes}
                onChange={(e) => setProjectField(i, 'notes', e.target.value)}
                disabled={!isEditing}
                className="w-full mt-2 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const renderNotesTab = () => (
    <section className={sectionClass}>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Notes</h2>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={12}
        disabled={!isEditing}
        className={inputClass}
        placeholder="Internal notes about this partner…"
      />
    </section>
  );

  return (
    <div className="min-h-screen p-4 relative">
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto">
      <div className="relative mb-6 min-h-[2.5rem]">
        <button
          type="button"
          onClick={() => navigate('/crm/partners')}
          title="Back to partners"
          className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 hover:border-red-400 dark:hover:border-red-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white pt-1 px-14">
          {companyName || 'Partner'}
        </h1>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 hover:border-red-400 dark:hover:border-red-500 text-sm font-medium transition-colors"
          >
            <Pencil className="w-4 h-4" />
            <span className="hidden sm:inline">Edit</span>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex justify-center flex-wrap gap-x-6 sm:gap-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  active
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      <form onSubmit={handleSaveOverview}>
        <div className="mb-8">
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'documents' && renderDocumentsTab()}
          {activeTab === 'billing' && renderBillingTab()}
          {activeTab === 'projects' && renderProjectsTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </div>

        {isEditing && (
          <div className="flex justify-end gap-2 sticky bottom-4">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                load();
              }}
              className="px-6 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium shadow-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg font-medium shadow-lg"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </form>

      {/* Fullscreen document viewer */}
      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
      />

      {/* New / rename folder */}
      <Modal isOpen={!!folderModal} onClose={() => setFolderModal(null)} maxWidth="md">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {folderModal?.id ? 'Rename folder' : 'New folder'}
        </h2>
        <input
          autoFocus
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              saveFolder();
            }
          }}
          placeholder="Folder name"
          className={inputClass}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={() => setFolderModal(null)} className={modalCancelBtn}>
            Cancel
          </button>
          <button type="button" onClick={saveFolder} disabled={!folderName.trim()} className={modalPrimaryBtn}>
            {folderModal?.id ? 'Save' : 'Create'}
          </button>
        </div>
      </Modal>

      {/* Rename + tag a document (opens on the row and right after an upload) */}
      <Modal isOpen={!!tagDocId} onClose={closeDocEditor} maxWidth="md">
        {(() => {
          const tagDoc = documents.find((d) => d.id === tagDocId);
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rename &amp; tags</h2>

              <label className={labelClass}>Name</label>
              <input
                autoFocus
                value={docNameDraft}
                onChange={(e) => setDocNameDraft(e.target.value)}
                onBlur={commitDocRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Document name"
                className={`${inputClass} mb-5`}
              />

              <div className="mb-2">
                <label className={`${labelClass} mb-0`}>Tags</label>
              </div>
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {DOC_TAGS.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={(tagDoc?.tagIds || []).includes(t.id)}
                        onChange={() => tagDoc && toggleDocTag(tagDoc.id, t.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="text-sm text-gray-900 dark:text-white">{t.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end mt-5">
                <button type="button" onClick={closeDocEditor} className={modalPrimaryBtn}>
                  Done
                </button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Move a document to a folder */}
      <Modal isOpen={!!moveDocId} onClose={() => setMoveDocId(null)} maxWidth="md">
        {(() => {
          const moveDoc = documents.find((d) => d.id === moveDocId);
          const folderOption =
            'w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-default text-gray-900 dark:text-white';
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Move to folder</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 truncate">{moveDoc?.name}</p>
              <div className="space-y-1">
                <button
                  type="button"
                  disabled={!(moveDoc?.folderId || '')}
                  onClick={() => moveDoc && moveDocumentToFolder(moveDoc.id, '')}
                  className={folderOption}
                >
                  <FileText className="w-4 h-4 text-gray-400" /> All files (no folder)
                </button>
                {folders.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    disabled={moveDoc?.folderId === f.id}
                    onClick={() => moveDoc && moveDocumentToFolder(moveDoc.id, f.id)}
                    className={folderOption}
                  >
                    <Folder className="w-4 h-4 text-amber-500" /> {f.name}
                  </button>
                ))}
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Delete document confirmation */}
      <Modal isOpen={!!deleteDocId} onClose={() => setDeleteDocId(null)} maxWidth="md">
        {(() => {
          const delDoc = documents.find((d) => d.id === deleteDocId);
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete document?</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
                “{delDoc?.name || 'This document'}” will be permanently removed. This can’t be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setDeleteDocId(null)} className={modalCancelBtn}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveDocument(deleteDocId)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Add / edit expense — client expenses write to that client's Billing tab */}
      <Modal isOpen={!!expenseModal} onClose={() => !expenseSaving && setExpenseModal(null)} maxWidth="md">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
          {expenseModal?.original ? 'Edit expense' : 'Add expense'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Client</label>
            <select
              value={expenseForm.accountChoice}
              onChange={(e) => setExpenseForm({ ...expenseForm, accountChoice: e.target.value })}
              className={inputClass}
            >
              <option value="">Select a client…</option>
              <option value={INTERNAL_EXPENSE}>Internal (no client)</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company}
                </option>
              ))}
            </select>
            {expenseForm.accountChoice && expenseForm.accountChoice !== INTERNAL_EXPENSE && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Also appears on this client’s Billing tab and counts toward their expenses.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                className={inputClass}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select
              value={expenseForm.status}
              onChange={(e) => setExpenseForm({ ...expenseForm, status: e.target.value })}
              className={inputClass}
            >
              <option value="paid">Paid</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <input
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              className={inputClass}
              placeholder="What was this for?"
            />
          </div>
        </div>
        {expenseError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{expenseError}</p>}
        <div className="mt-6 flex items-center gap-2">
          {expenseModal?.original && (
            <button
              type="button"
              onClick={handleDeleteExpense}
              disabled={expenseSaving}
              className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 disabled:opacity-50"
            >
              Delete
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => setExpenseModal(null)}
              disabled={expenseSaving}
              className={modalCancelBtn}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveExpense}
              disabled={expenseSaving || !expenseForm.amount || !expenseForm.accountChoice}
              className={modalPrimaryBtn}
            >
              {expenseSaving ? 'Saving…' : expenseModal?.original ? 'Update expense' : 'Add expense'}
            </button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default PartnerOverview;
